import type { z } from 'zod'

import { envelopeSchema } from './schemas/common'

export class ApiError extends Error {
  readonly code: string
  readonly status?: number

  constructor(code: string, message: string, status?: number, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

function formatIssues(error: z.ZodError): string {
  return error.issues
    .slice(0, 5)
    .map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`)
    .join('; ')
}

/**
 * Разбирает ответ и валидирует его по zod-схеме.
 * AZS API отдаёт данные плоско; backend2.0 — в обёртке { success, data, error }.
 * Поддерживаем оба варианта: обёртку узнаём по булеву success.
 * Ответ не по схеме → понятная ошибка вместо тихого undefined в интерфейсе.
 */
export function parseApiResponse<T>(raw: unknown, schema: z.ZodType<T>, status?: number): T {
  const envelope = envelopeSchema.safeParse(raw)
  const payload = envelope.success ? envelope.data.data : raw

  if (envelope.success && !envelope.data.success) {
    const error = envelope.data.error
    throw new ApiError(
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Запрос завершился ошибкой',
      status,
    )
  }

  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    throw new ApiError(
      'INVALID_RESPONSE',
      `Данные не соответствуют схеме: ${formatIssues(parsed.error)}`,
      status,
    )
  }
  return parsed.data
}
